import {
  DiseaseGroupResponseDto,
  TherapistSearchRequestDto,
  TherapistWorkingInfoResponseDtoWorkWithReligionsEnum,
  TherapyMethodResponseDto,
} from '@app/api';

import { SELECT_OPTIONS, VARS } from '@app/constants';
import { IFilter } from '@app/types';
import { castToArray } from '@app/utils/castToArray';
import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { ConvertAppliedFiltersToQueryParams, DayLimitEnum } from './types';
import { setKeyValueInObject } from '@app/utils/objectsTricks';
import { UserGender } from '../TherapistDetailsPage/types';

export const convertTherapyMethodsToFilterOptions = (
  therapyMethods: TherapyMethodResponseDto[],
): IFilter => {
  return {
    name: 'therapyMethods',
    title: 'Метод терапии',
    options: therapyMethods.map((item) => ({
      id: item.id,
      name: item.name,
    })),
  };
};

export const convertDiseasesGroupsToFilterOptions = (
  diseasesGroups: DiseaseGroupResponseDto[],
): IFilter[] => {
  return diseasesGroups.map((item, index) => ({
    name: `diseases.${index}`,
    title: item.name,
    options: item.items.map((item) => ({
      id: item?.id,
      name: item.name,
      url: item?.url,
    })),
  }));
};

export const convertDayLimitToQueryValue = (dayLimit: DayLimitEnum) => {
  switch (dayLimit) {
    case DayLimitEnum.Month:
      return 30;

    case DayLimitEnum.Week:
      return 7;

    case DayLimitEnum.ThreeDays:
      return 3;

    case DayLimitEnum.Today:
      return 1;

    case DayLimitEnum.NoMatter:

    default:
      return undefined;
  }
};

export const convertPriceToQueryValue = (price: number) => {
  return {
    priceMax: price,
  };
};

export const convertReligionsToQueryValue = (religion: string) => {
  switch (religion) {
    case VARS.russianTranslateReligions['buddhism']:
      return TherapistWorkingInfoResponseDtoWorkWithReligionsEnum.Buddhism;
    case VARS.russianTranslateReligions['islamism']:
      return TherapistWorkingInfoResponseDtoWorkWithReligionsEnum.Islamism;
    case VARS.russianTranslateReligions['judaism']:
      return TherapistWorkingInfoResponseDtoWorkWithReligionsEnum.Judaism;
    default:
      return TherapistWorkingInfoResponseDtoWorkWithReligionsEnum.Christianity;
  }
};

export function convertAppliedFiltersToQueryParams({
  selectedFiltersValues,
  selectedFiltersDiseases,
  page,
  selectedSorting,
  tab,
  searchValue,
  childOld,
  therapistOld,
}: ConvertAppliedFiltersToQueryParams): Partial<TherapistSearchRequestDto> {
  let filters = {};

  if (page && page !== 1) {
    filters = setKeyValueInObject('page', page ? page : 1, filters);
  }

  if (
    selectedFiltersValues?.['therapistOld'] &&
    selectedFiltersValues?.['therapistOld'] != 'any'
  )
    filters = setKeyValueInObject(
      'therapistOld',
      selectedFiltersValues?.['therapistOld'],
      filters,
    );

  if (childOld) {
    filters = setKeyValueInObject('childOld', childOld, filters);
  }

  if (therapistOld) {
    filters = setKeyValueInObject('therapistOld', therapistOld, filters);
  }

  if (selectedFiltersDiseases?.length)
    filters = setKeyValueInObject('diseases', selectedFiltersDiseases, filters);

  if (!!selectedFiltersValues?.['workWithLgbt']?.[0])
    filters = setKeyValueInObject(
      'workWithLgbt',
      !!selectedFiltersValues?.['workWithLgbt']?.[0],
      filters,
    );

  if (
    selectedFiltersValues?.['gender'] &&
    selectedFiltersValues?.['gender'] != UserGender.ANY
  )
    filters = setKeyValueInObject(
      'gender',
      selectedFiltersValues?.['gender'],
      filters,
    );

  if (!!selectedFiltersValues?.['workWithEthnicGroups']?.[0])
    filters = setKeyValueInObject(
      'workWithEthnicGroups',
      !!selectedFiltersValues?.['workWithEthnicGroups']?.[0],
      filters,
    );

  if (!!selectedFiltersValues?.['isApproved']?.[0])
    filters = setKeyValueInObject(
      'isApproved',
      !!selectedFiltersValues?.['isApproved']?.[0],
      filters,
    );

  if (
    !!selectedFiltersValues?.['dayLimit'] &&
    selectedFiltersValues?.['dayLimit'] !== 'any'
  )
    filters = setKeyValueInObject(
      'dayLimit',
      selectedFiltersValues['dayLimit'],
      filters,
    );

  if (!!selectedFiltersValues?.['priceMax'])
    filters = setKeyValueInObject(
      'priceMax',
      selectedFiltersValues?.['priceMax'],
      filters,
    );

  if (selectedFiltersValues?.['religions']?.length)
    filters = setKeyValueInObject(
      'religions',
      selectedFiltersValues?.['religions'],
      filters,
    );

  if (selectedFiltersValues?.['therapyMethods']?.length)
    filters = setKeyValueInObject(
      'therapyMethods',
      selectedFiltersValues?.['therapyMethods'],
      filters,
    );

  if (selectedSorting?.order && selectedSorting.orderTerms) {
    filters = setKeyValueInObject('order', selectedSorting.order, filters);
    filters = setKeyValueInObject(
      'orderTerms',
      selectedSorting.orderTerms,
      filters,
    );
  }

  if (tab) filters = setKeyValueInObject('tab', tab, filters);
  if (searchValue)
    filters = setKeyValueInObject('search', searchValue, filters);

  console.log('utils convertAppliedFiltersToQueryParams filters', filters);
  return filters;
}

export const mergeInitialFiltersAndFieldValues = ({
  filters,
  data,
}: {
  data: FieldValues;
  filters: IFilter[];
}): IFilter[] => {
  let mergedFilters = [...filters];

  const dataCopy = { ...data };
  const dataEntries = Object.entries(dataCopy);

  dataEntries?.forEach(([key, value]) => {
    const validValue = value;

    if (!validValue?.length) return;

    mergedFilters = mergedFilters.map((item) => {
      if (item.name.includes(key)) {
        const isValueArrayOfArrays =
          Array.isArray(validValue) &&
          validValue.some((item) => Array.isArray(item));

        if (isValueArrayOfArrays) {
          const index = +item.name.split('.')?.[1];
          const checkedValues = validValue[index];

          if (checkedValues) return { ...item, checked: checkedValues };
        } else {
          const requiredValue = Array.isArray(validValue)
            ? validValue.filter((item) => item)
            : [validValue];

          if (requiredValue?.length) {
            return { ...item, checked: requiredValue };
          }
        }
      }

      return item;
    });
  });

  return mergedFilters;
};

export const mergeFilterIcons = (
  filterIcons: Array<{ icon: ReactNode; name: string; tooltip: ReactNode }>,
  filters: IFilter[],
) => {
  return filters.map((filter) => {
    const name = filter.name;
    const requiredItem = filterIcons.find((item) => item.name === name);

    if (requiredItem)
      return {
        ...filter,
        icon: requiredItem.icon,
        tooltip: requiredItem.tooltip,
      };

    return { ...filter };
  });
};

export const formatDiseasesFromQueryToFilter = (
  diseasesUrls: string[] | string,
  filters: IFilter[],
  diseasesGroups: DiseaseGroupResponseDto[],
) => {
  const diseasesWithGroupName = diseasesGroups
    .map((group) => {
      return group.items.map((item) => {
        return {
          name: item.name,
          id: item.id,
          url: item.url,
          groupName: group.name,
        };
      });
    })
    .reduce((a, c) => a.concat(c), []);

  const isOnlyOneDiseaseSelected = typeof diseasesUrls === 'string';

  const findDiseaseGroupName = (diseasesUrl: string) => {
    const diseases = diseasesWithGroupName.find(
      (diseases) => diseases.url === diseasesUrl,
    );

    const filterName = filters.find(
      (filter) => filter.title === diseases?.groupName,
    )?.name;

    let index = 0;

    if (filterName?.includes('.')) {
      index = Number.parseInt(filterName.split('.')[1]);
    }

    return { diseases, index };
  };

  if (isOnlyOneDiseaseSelected) {
    const { diseases, index } = findDiseaseGroupName(diseasesUrls);

    return (
      diseases && {
        name: diseases.name,
        groupName: diseases.groupName,
        index,
      }
    );
  } else {
    const diseasesForFilter = diseasesUrls?.map((diseasesUrl: string) => {
      const { diseases, index } = findDiseaseGroupName(diseasesUrl);

      return (
        diseases && {
          name: diseases.name,
          url: diseases?.url,
          id: diseases?.id,
          groupName: diseases.groupName,
          index,
        }
      );
    });

    const result = new Array<string[] | undefined>(3).fill(undefined);

    diseasesForFilter.forEach((diseases) => {
      if (!diseases) return;

      let arr = result[diseases.index];

      const identificator = diseases?.url
        ? diseases.url
        : diseases?.id
        ? diseases.id
          ? diseases?.name
          : diseases.name
        : diseases.toString();

      if (arr) {
        arr.push(identificator);
      } else {
        arr = [identificator];
      }

      result[diseases.index] = arr;
    });

    return result;
  }
};

export const formatReligionsFromQueryToFilter = (
  religions: string[] | string,
) => {
  return castToArray(religions).map((religionFromQuery) => {
    return SELECT_OPTIONS.religions.find(
      (religion) => religion.value === religionFromQuery,
    )?.label;
  });
};

export const formatQueryParamsToFilter = (params: {
  query: {
    diseases?: string[] | string;
    religions?: string[] | string;
  };
  filters: IFilter[];
  diseasesGroups: DiseaseGroupResponseDto[];
}) => {
  const { query, filters, diseasesGroups } = params;
  //// console.log('formatQueryParamsToFilter filters',filters);
  return {
    diseases:
      query.diseases &&
      formatDiseasesFromQueryToFilter(query.diseases, filters, diseasesGroups),
    religions:
      query.religions && formatReligionsFromQueryToFilter(query.religions),
  };
};
